import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SEO } from '../seo/SEO';
import { useI18n } from '../i18n';
import { useAuth } from '../auth';
import { AppHeader } from '../components/AppHeader';
import { useSmartBack } from '../hooks/useSmartBack';
import { useRoutes } from '../hooks/useRoutes';
import { useAppModal } from '../components/Modal';
import './PremiumPage.css';

const API = import.meta.env.VITE_API_URL || 'http://localhost:8000';

type Tier = 'monthly' | 'yearly';

export function PremiumPage() {
  const { t } = useI18n();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const { r } = useRoutes();
  const smartBack = useSmartBack('/');
  const { showAlert, modalElement } = useAppModal();
  const [busy, setBusy] = useState<Tier | null>(null);
  const [status, setStatus] = useState<{ is_premium: boolean; expires_at: string | null; platform: string | null } | null>(null);

  useEffect(() => {
    if (!token) return;
    fetch(`${API}/premium/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then(setStatus)
      .catch(() => {});
  }, [token]);

  const isActive = !!(status?.is_premium ?? user?.is_premium);

  const handleBuy = async (tier: Tier) => {
    if (!token) {
      navigate('/auth');
      return;
    }
    setBusy(tier);
    try {
      await showAlert(t.premium.webPaymentSoon, t.premium.title);
    } finally {
      setBusy(null);
    }
  };

  const features = [
    { title: (t.premium as any).feature1, desc: (t.premium as any).feature1Desc, icon: '✨' },
    { title: (t.premium as any).feature2, desc: (t.premium as any).feature2Desc, icon: '🧠' },
    { title: (t.premium as any).feature3, desc: (t.premium as any).feature3Desc, icon: '📚' },
    { title: (t.premium as any).feature4, desc: (t.premium as any).feature4Desc, icon: '🎨' },
    { title: (t.premium as any).feature5, desc: (t.premium as any).feature5Desc, icon: '🛠️' },
    { title: (t.premium as any).feature6, desc: (t.premium as any).feature6Desc, icon: '⭐' },
  ];

  return (
    <>
      <SEO path="/premium" />
      <AppHeader title={t.premium.title} onBack={smartBack} backLabel={t.common.back} />
      <main className="premium-page">
        <header className="premium-hero">
          <div className="premium-hero-badge">PRO</div>
          <h1>{t.premium.title}</h1>
          <p className="premium-hero-tag">{(t.premium as any).heroTagline ?? t.premium.subtitle}</p>
          {isActive && (
            <div className="premium-active-pill">
              ✓ {t.premium.active}
              {status?.expires_at && (
                <span className="premium-active-expires">
                  {' '}· {t.premium.expires.replace('{date}', new Date(status.expires_at).toLocaleDateString())}
                </span>
              )}
            </div>
          )}
        </header>

        <section className="premium-features">
          {features.map((f, i) => (
            <article key={i} className="premium-feature-card">
              <div className="premium-feature-icon">{f.icon}</div>
              <div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            </article>
          ))}
        </section>

        <section className="premium-tiers">
          <article className="premium-tier">
            <h3>{t.premium.monthly}</h3>
            <div className="premium-tier-price">{t.premium.monthlyPrice}</div>
            <button
              className="premium-buy-btn"
              disabled={busy !== null || isActive}
              onClick={() => handleBuy('monthly')}
            >
              {busy === 'monthly' ? '…' : t.premium.buyMonthly}
            </button>
          </article>
          <article className="premium-tier premium-tier--featured">
            <span className="premium-tier-badge">{t.premium.yearlyDiscount}</span>
            <h3>{t.premium.yearly}</h3>
            <div className="premium-tier-price">{t.premium.yearlyPrice}</div>
            <button
              className="premium-buy-btn premium-buy-btn--gold"
              disabled={busy !== null || isActive}
              onClick={() => handleBuy('yearly')}
            >
              {busy === 'yearly' ? '…' : t.premium.buyYearly}
            </button>
          </article>
        </section>

        <section className="premium-legal">
          <button className="premium-legal-link" onClick={() => navigate(r('privacy'))}>
            {t.premium.privacyLink}
          </button>
          <span className="premium-legal-sep">·</span>
          <a
            className="premium-legal-link"
            href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t.premium.termsLink}
          </a>
        </section>
      </main>
      {modalElement}
    </>
  );
}

export default PremiumPage;
